import React, { useEffect, useState } from 'react';
import lodash from 'lodash';
import { connect } from 'react-redux';
import classnames from 'classnames';
import styles from './Events.scss';
import { Button, BUTTON } from 'components/common/Button/Button';
import { ComposablePageHeader, MainContent, SideContent } from 'components/common/PageHeader/PageHeader';
import SelectionModal from '../../../components/common/SelectionModal/SelectionModal';
import DismissiblePanel from 'components/common/DismissiblePanel';
import {
    binderInstitutionIdSelector,
    getTimezoneOffset,
} from 'common/selectors';

import { EventsList } from './EventsList';
import EventPanel from './EventPanel';
import {
    activeEventLoadStart,
    activeEventLoadEnd,
    clearActiveEvent,
    clearEvents,
    exportAllEvents,
    selectActiveEvent,
    getEvents,
    loadActiveEventTypes
} from '../actions/events';
import { Conditional } from '../../../common/components/layout';
import { manageStudyEventsPredicate } from '../../../common/permissions/studyPredicates';
import { loadHomePageV4Access } from '../../../common/features/predicates';

export const Events = (props) => {
    const [showExportModal, setShowExportModal] = useState(false);
    const [showPanel, setShowPanel] = useState(false);
    const [view, setView] = useState('details');

    useEffect(() => {
        const {
            institutionId,
            binderId,
            clearEvents,
            getEvents
        } = props;

        props.setSidebarCollapsed && props.setSidebarCollapsed(true);
        clearEvents();
        getEvents(institutionId, binderId);

        return function cleanup() {
            props.setSidebarCollapsed && props.setSidebarCollapsed(false);
            props.clearEvents();
        };
    }, []);

    function selectEvent(event) {
        setView('details');
        props.selectActiveEvent(event.id);
    }

    function selectNewEvent() {
        setView('details');
        props.selectActiveEvent(null);
    }

    function exportEventType(eventTypeId) {
        setShowExportModal(showExportModal => showExportModal = false);
        const { binderId, institutionId } = props;
        props.exportAllEvents(binderId, institutionId, eventTypeId);
    }

    function displayExportModal() {
        const { institutionId, studyId, binderId, isStudyEvent } = props;

        let options = { institutionId };

        if (isStudyEvent) {
            options.studyId = studyId;
        }
        else {
            options.binderId = binderId;
        }

        // Fetch currently active events to display in the modal.
        props.loadActiveEventTypes(options);

        // Open the export events modal.
        setShowExportModal(showExportModal => showExportModal = true);
    }

    function closeExportModal() {
        setShowExportModal(showExportModal => showExportModal = false);
    }

    function getExportEventTypeOptions() {
        const { studyEventsActiveEventTypes } = props;

        if (lodash.isEmpty(studyEventsActiveEventTypes)) {
            return [];
        }

        const exportEventTypeOptions = studyEventsActiveEventTypes.map((eventType) => {
            return {
                value: eventType.id,
                label: eventType.displayName,
                enabled: true
            };
        });
        return lodash.sortBy(exportEventTypeOptions, 'label');
    }


    const {
        events,
        timezoneOffset,
        exportInProgress,
        slatsLoading,
        icfLanguages,
        hasManageEvents,
        complion4FlagEnabled,
    } = props;

    const exportButtonDisabled = exportInProgress || events.length === 0;
    const exportEventTypeOptions = getExportEventTypeOptions();

    return (
        <main className={styles.parent} data-cy="EventHistoryPanel-main">
            <ComposablePageHeader className={styles.header}>
                <MainContent>
                    <h1 className={styles.title} data-cy="EventHistoryPanel-h1">{props.studyId && !props.complion4FlagEnabled ? 'Study ' : 'Binder'} Events</h1>
                    <Conditional condition={hasManageEvents}>
                        <Button
                            bgcolor={BUTTON.BGCOLOR.BLUE}
                            color={BUTTON.COLOR.WHITE}
                            onClick={() => {
                                if (showPanel) {
                                    props.activeEventLoadStart();
                                    selectNewEvent();
                                    setTimeout(() => {
                                        props.activeEventLoadEnd();
                                    }, 150);
                                }

                                props.clearActiveEvent();
                                setShowPanel(true);
                            }}
                            data-cy="EventHistoryPanel-Button"
                        >
                            Add New {props.studyId && !props.complion4FlagEnabled ? 'Study ' : 'Binder'} Event
                        </Button>
                    </Conditional>
                </MainContent>
                <SideContent>
                    <div className={styles.flushRight}>
                        <button className={styles.events__button__export}
                            onClick={() => displayExportModal()}
                            disabled={exportButtonDisabled}
                            data-cy="EventHistoryPanel-ExportButton">
                            Export Events
                        </button>
                    </div>
                </SideContent>
            </ComposablePageHeader>
            <div className={
                classnames(
                    styles.eventContainer,
                    { [styles.panelOpen]: showPanel, }
                )
            } data-cy="EventHistoryPanel-div">
                <EventsList
                    events={events}
                    icfLanguages={icfLanguages}
                    onSelect={(event) => selectEvent(event)}
                    showPanel={(bool) => setShowPanel(bool)}
                    timezoneOffset={timezoneOffset}
                    loading={slatsLoading}
                    hasManageEvents={hasManageEvents}
                    complion4FlagEnabled={complion4FlagEnabled}
                />
                {
                    <DismissiblePanel
                        className={styles.addEventPanel}
                        dismiss={!showPanel}
                        onDismiss={() => setShowPanel(false)}
                    >
                        <EventPanel
                            closePanel={() => setShowPanel()}
                            setView={setView}
                            view={view}
                            icfLanguages={icfLanguages}
                            hasManageEvents={hasManageEvents}
                        />
                    </DismissiblePanel>
                }
            </div>
            {showExportModal ? (
                <SelectionModal
                    options={exportEventTypeOptions}
                    onSubmit={(eventTypeId) => {
                        exportEventType(eventTypeId);
                    }}
                    onClose={() => closeExportModal()}
                />
            ) : null}

        </main>
    );
};


export default connect(
    state => {
        const binderId = lodash.get(state, 'binder.id');
        const events = lodash.get(state, 'studyEvents.eventData.list', []);
        const exportInProgress = lodash.get(state, 'studyEvents.exportInProgress', false);
        const institutionId = binderInstitutionIdSelector(state);
        const slatsLoading = lodash.get(state, 'studyEvents.slatsLoading', false);
        const timezoneOffset = getTimezoneOffset(state);
        const eventTypes = lodash.get(state, 'studyEvents.referenceData.eventTypes', {});
        const icfLanguages = lodash.get(state, 'studyEvents.eventData.icfLanguages', []);
        const studyId = lodash.get(state, 'study.id');
        const hasManageEvents = studyId ? manageStudyEventsPredicate(state) : true;
        const studyEventsActiveEventTypes = lodash.get(state, 'studyEvents.eventData.studyEventsActiveEventTypes', []);
        return {
            binderId,
            events,
            exportInProgress,
            institutionId,
            slatsLoading,
            timezoneOffset,
            eventTypes,
            icfLanguages,
            hasManageEvents,
            studyId,
            studyEventsActiveEventTypes,
            complion4FlagEnabled: loadHomePageV4Access(state),
        };
    },
    {
        activeEventLoadStart,
        activeEventLoadEnd,
        clearActiveEvent,
        clearEvents,
        exportAllEvents,
        selectActiveEvent,
        getEvents,
        loadActiveEventTypes,
    }
)(Events);
